import { format, formatDistanceToNow, isValid, parseISO } from "date-fns";

/**
 * Format kobo (integer minor units) as a presentable naira string.
 * Backend stores money as bigint kobo (1 kobo = 1/100 ₦). UI always renders
 * via this helper so admin and tenant portals agree on the same format.
 *
 *   formatNaira(2400000)  // "₦24,000"
 *   formatNaira(60000000) // "₦600,000"
 *   formatNaira(null)     // "—"
 */
export function formatNaira(
  kobo: number | string | null | undefined,
  options: { fallback?: string } = {},
): string {
  if (kobo === null || kobo === undefined) return options.fallback ?? "—";
  const n = typeof kobo === "string" ? Number(kobo) : kobo;
  if (!Number.isFinite(n)) return options.fallback ?? "—";
  return "₦" + Math.round(n / 100).toLocaleString("en-NG");
}

/**
 * Format an ISO date or datetime as "MMM d, yyyy" (e.g. "Apr 22, 2026").
 * Accepts both YYYY-MM-DD and full ISO 8601.
 */
export function formatDate(
  iso: string | null | undefined,
  pattern: string = "MMM d, yyyy",
): string {
  if (!iso) return "—";
  const d = parseISO(iso);
  if (!isValid(d)) return "—";
  return format(d, pattern);
}

/**
 * Short date format: "22 Apr". Useful for compact rows.
 */
export function formatShortDate(iso: string | null | undefined): string {
  return formatDate(iso, "d MMM");
}

/**
 * Relative time: "3 days ago", "in 2 weeks".
 */
export function formatRelativeDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseISO(iso);
  if (!isValid(d)) return "—";
  return formatDistanceToNow(d, { addSuffix: true });
}

/**
 * Days from today until (positive) or since (negative) the given date.
 * Returns null on invalid input.
 */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = parseISO(iso);
  if (!isValid(d)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}
